import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Typography,
  Chip,
  Avatar,
  Stack,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  InputAdornment,
  SelectChangeEvent,
} from "@mui/material";
import { Lock as LockIcon, Edit as EditIcon, Search as SearchIcon } from "@mui/icons-material";
import axiosInstance from "utilities/axios";
import DataTable, { ColumnDef } from "components/common/DataTable";
import ProtectedPage from "components/common/ProtectedPage";
import PermissionsCheckboxGroup, { Permission } from "components/dialogs/PermissionsCheckboxGroup";
import { formatUserForDisplay, USER_TYPE_CHIP, PERMISSION_LABEL } from "utils/userFormatting";
import "./Permissions.scss";

type User = { _id: string; name: string; email: string; permissions: string[]; [key: string]: any };

const ALL_PERMISSIONS: Permission[] = [
  { name: "მომხმარებლების მართვა",         value: "USER_MANAGE" },
  { name: "კომპანიების მართვა",             value: "MANAGE_MONITORING_COMPANIES" },
  { name: "ფორმის წარდგენა",               value: "SUBMIT_MONITORING_FORMS" },
  { name: "განხილვა",                       value: "REVIEW_MONITORING" },
  { name: "გადამისამართება",               value: "REASSIGN_APPLICATIONS" },
  { name: "გადახდების მართვა",             value: "MANAGE_PAYMENTS" },
  { name: "შეთანხმებების მართვა",          value: "MANAGE_MONITORING_AGREEMENTS" },
];

function getInitials(name: string) {
  return name.split(" ").slice(0, 2).map((n) => n[0] || "").join("").toUpperCase();
}

function Permissions() {
  const [loading, setLoading]           = useState(false);
  const [users, setUsers]               = useState<User[]>([]);
  const [total, setTotal]               = useState(0);
  const [search, setSearch]             = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [filterUserType, setFilterUserType]   = useState("");

  const [editUser, setEditUser]             = useState<User | null>(null);
  const [editPermissions, setEditPermissions] = useState<string[]>([]);
  const [saving, setSaving]                 = useState(false);

  useEffect(() => {
    const handler = setTimeout(() => setDebouncedSearch(search), 500);
    return () => clearTimeout(handler);
  }, [search]);

  const fetchUsers = useCallback(() => {
    setLoading(true);
    axiosInstance
      .post("/fetch_users", { page: 0, limit: 200, search: debouncedSearch, userType: filterUserType || undefined })
      .then((res) => {
        const responseData = res.data.users || res.data;
        const list = (Array.isArray(responseData) ? responseData : []).map(formatUserForDisplay);
        setUsers(list);
        setTotal(res.data.total ?? list.length);
        setLoading(false);
      })
      .catch((err) => { console.error(err); setLoading(false); });
  }, [debouncedSearch, filterUserType]);

  useEffect(() => { fetchUsers(); }, [fetchUsers]);

  function openEdit(user: User) {
    setEditUser(user);
    setEditPermissions([...(user.permissions || [])]);
  }

  async function handleSave() {
    if (!editUser) return;
    setSaving(true);
    try {
      await axiosInstance.put(`/auth/user/${editUser._id}`, { permissions: editPermissions });
      setEditUser(null);
      fetchUsers();
    } catch (err: any) {
      alert(err?.response?.data?.message || "შეცდომა");
    } finally {
      setSaving(false);
    }
  }

  const columns: ColumnDef<User>[] = [
    {
      id: "avatar",
      label: "",
      width: 44,
      render: (row) => (
        <Avatar sx={{ width: 30, height: 30, fontSize: 12, bgcolor: row.user_type === "monitoring" ? "secondary.main" : "grey.400" }}>
          {getInitials(row.name || "")}
        </Avatar>
      ),
    },
    {
      id: "name",
      label: "სახელი / ელ-ფოსტა",
      render: (row) => (
        <>
          <Typography variant="body2" fontWeight={500}>{row.name}</Typography>
          <Typography variant="caption" color="text.secondary">{row.email}</Typography>
        </>
      ),
    },
    {
      id: "type",
      label: "ტიპი",
      align: "center",
      render: (row) =>
        row.user_type && USER_TYPE_CHIP[row.user_type] ? (
          <Chip
            label={USER_TYPE_CHIP[row.user_type].label}
            color={USER_TYPE_CHIP[row.user_type].color}
            size="small"
            variant="outlined"
            sx={{ fontSize: 11, height: 20 }}
          />
        ) : null,
    },
    {
      id: "org",
      label: "ორგანიზაცია",
      render: (row) => <Typography variant="body2">{row.bankDisplay}</Typography>,
    },
    {
      id: "permissions",
      label: "უფლებები",
      render: (row) => (
        <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
          {(row.permissions || []).length === 0 ? (
            <Typography variant="caption" color="text.disabled">—</Typography>
          ) : (
            (row.permissions || []).map((p: string) => (
              <Chip key={p} label={PERMISSION_LABEL[p] ?? p} size="small" variant="outlined" sx={{ fontSize: 10, height: 18 }} />
            ))
          )}
        </Stack>
      ),
    },
    {
      id: "actions",
      label: "",
      width: 52,
      align: "center",
      render: (row) => (
        <Tooltip title="უფლებების რედაქტირება">
          <IconButton size="small" onClick={() => openEdit(row)} sx={{ p: 0.5, "& svg": { fontSize: 15 } }}>
            <EditIcon />
          </IconButton>
        </Tooltip>
      ),
    },
  ];

  return (
    <ProtectedPage permission="USER_MANAGE">
      <Box sx={{ p: 3 }}>
        <div className="permissions-page__header">
          <div className="permissions-page__title-group">
            <LockIcon color="primary" sx={{ fontSize: 28 }} />
            <Typography variant="h5" fontWeight={700}>უფლებები</Typography>
            {total > 0 && (
              <Chip label={total} size="small" color="primary" variant="outlined" sx={{ fontWeight: 600, height: 22, fontSize: 12 }} />
            )}
          </div>

          <div className="permissions-page__filters">
            <FormControl size="small" sx={{ minWidth: 160 }}>
              <InputLabel>ტიპი</InputLabel>
              <Select value={filterUserType} label="ტიპი" onChange={(e: SelectChangeEvent) => setFilterUserType(e.target.value)}>
                <MenuItem value=""><em>ყველა</em></MenuItem>
                <MenuItem value="agency">სააგენტო</MenuItem>
                <MenuItem value="monitoring">მონიტ. კომპანია</MenuItem>
                <MenuItem value="admin">ადმინი</MenuItem>
              </Select>
            </FormControl>
            <TextField
              placeholder="ძებნა..."
              size="small"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" color="action" /></InputAdornment> }}
              sx={{ width: 220 }}
            />
          </div>
        </div>

        <DataTable
          columns={columns}
          rows={users}
          rowKey={(row) => row._id}
          loading={loading}
          emptyText="მომხმარებლები არ მოიძებნა"
        />

        <Dialog open={!!editUser} onClose={() => setEditUser(null)} maxWidth="xs" fullWidth>
          <DialogTitle>
            <Typography fontWeight={700}>უფლებების რედაქტირება</Typography>
            {editUser && (
              <Typography variant="body2" color="text.secondary">{editUser.name}</Typography>
            )}
          </DialogTitle>
          <DialogContent>
            <PermissionsCheckboxGroup
              permissions={ALL_PERMISSIONS}
              userPermissions={editPermissions}
              setUserPermissions={setEditPermissions}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setEditUser(null)} sx={{ textTransform: "none" }}>გაუქმება</Button>
            <Button
              variant="contained"
              disabled={saving}
              onClick={handleSave}
              sx={{ textTransform: "none", fontWeight: 600 }}
            >
              შენახვა
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </ProtectedPage>
  );
}

export default Permissions;
